import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Send, 
  MessageCircle, 
  ThumbsUp, 
  ThumbsDown, 
  Star,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

interface CollaborationBoardResource {
  type: 'collaboration_board';
  name: string;
  title: string;
  description: string;
  features: string[];
  max_participants: number;
  moderation_enabled: boolean;
}

interface Message {
  id: string;
  author: string;
  content: string;
  timestamp: Date;
  type: 'message' | 'idea' | 'question' | 'comment';
  likes: number;
  dislikes: number;
}

interface CollaborationBoardComponentProps {
  resource: CollaborationBoardResource;
  onAction?: (action: string, data: any) => void;
}

export const CollaborationBoardComponent: React.FC<CollaborationBoardComponentProps> = ({
  resource,
  onAction
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState<'message' | 'idea' | 'question' | 'comment'>('message');
  // const [participants, setParticipants] = useState<string[]>(['Élève 1', 'Élève 2', 'Élève 3']);
  // const [isConnected, setIsConnected] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = useCallback(() => {
    if (!newMessage.trim()) return;

    const message: Message = {
      id: Date.now().toString(),
      author: 'Vous',
      content: newMessage.trim(),
      timestamp: new Date(),
      type: messageType,
      likes: 0,
      dislikes: 0
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');

    if (onAction) {
      onAction('message_sent', {
        messageId: message.id,
        type: messageType,
        content: message.content
      });
    }
  }, [newMessage, messageType, onAction]);

  const likeMessage = useCallback((messageId: string) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, likes: msg.likes + 1 }
        : msg
    ));

    if (onAction) {
      onAction('message_liked', { messageId });
    }
  }, [onAction]);

  const dislikeMessage = useCallback((messageId: string) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, dislikes: msg.dislikes + 1 }
        : msg
    ));

    if (onAction) {
      onAction('message_disliked', { messageId });
    }
  }, [onAction]);

  const getMessageIcon = (type: string) => {
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      message: MessageCircle,
      idea: Star,
      question: AlertCircle,
      comment: CheckCircle
    };
    return iconMap[type] || MessageCircle;
  };

  const getMessageColor = (type: string) => {
    const colorMap: { [key: string]: string } = {
      message: 'bg-blue-100 border-blue-200',
      idea: 'bg-yellow-100 border-yellow-200',
      question: 'bg-green-100 border-green-200',
      comment: 'bg-purple-100 border-purple-200'
    };
    return colorMap[type] || 'bg-gray-100 border-gray-200';
  };

  const getMessageTypeLabel = (type: string) => {
    const labelMap: { [key: string]: string } = {
      message: 'Message',
      idea: 'Idée',
      question: 'Question',
      comment: 'Commentaire'
    };
    return labelMap[type] || 'Message';
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">{resource.title}</h2>
        <p className="text-gray-600 mb-4">{resource.description}</p>
        
        {/* Statut de connexion */}
        {/* <div className="flex items-center justify-center space-x-2 mb-4">
          <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-sm text-gray-600">
            {isConnected ? 'Connecté' : 'Déconnecté'}
          </span>
        </div> */}
      </div>

      {/* Participants */}
      {/* <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <Users className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-900">Participants</h3>
          <span className="text-sm text-blue-600">
            ({participants.length}/{resource.max_participants})
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {participants.map((participant, index) => (
            <div
              key={index}
              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
            >
              {participant}
            </div>
          ))}
        </div>
      </div> */}

      {/* Fonctionnalités */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Fonctionnalités disponibles</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {resource.features.map((feature, index) => (
            <div key={index} className="flex items-center space-x-2 text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Zone de messages */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Messages de l'équipe</h3>
        
        {/* Messages */}
        <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Aucun message pour le moment</p>
              <p className="text-sm">Commencez la conversation !</p>
            </div>
          ) : (
            messages.map((message) => {
              const Icon = getMessageIcon(message.type);
              return (
                <div
                  key={message.id}
                  className={`p-3 rounded-lg border-2 ${getMessageColor(message.type)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon className="w-4 h-4" />
                      <span className="font-medium text-sm">{message.author}</span>
                      <span className="text-xs text-gray-500">
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {getMessageTypeLabel(message.type)}
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-2">{message.content}</p>
                  
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => likeMessage(message.id)}
                      className="flex items-center space-x-1 text-green-600 hover:text-green-700"
                    >
                      <ThumbsUp className="w-4 h-4" />
                      <span className="text-sm">{message.likes}</span>
                    </button>
                    <button
                      onClick={() => dislikeMessage(message.id)}
                      className="flex items-center space-x-1 text-red-600 hover:text-red-700"
                    >
                      <ThumbsDown className="w-4 h-4" />
                      <span className="text-sm">{message.dislikes}</span>
                    </button>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Formulaire de message */}
        <div className="border-t pt-4">
          <div className="flex space-x-2 mb-3">
            <select
              value={messageType}
              onChange={(e) => setMessageType(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="message">Message</option>
              <option value="idea">Idée</option>
              <option value="question">Question</option>
              <option value="comment">Commentaire</option>
            </select>
          </div>
          
          <div className="flex space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Tapez votre message..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim()}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Send className="w-4 h-4" />
              <span>Envoyer</span>
            </button>
          </div>
        </div>
      </div>

      {/* Modération */}
      {resource.moderation_enabled && (
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-yellow-700">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Modération activée</span>
          </div>
          <p className="text-sm text-yellow-600 mt-1">
            Les messages sont modérés pour assurer un environnement respectueux
          </p>
        </div>
      )}
    </div>
  );
};

export default CollaborationBoardComponent;
